import api from './api';

const departmentService = {
  // Get all departments with optional filtering and hierarchy
  getAllDepartments: async (params = {}) => {
    const response = await api.get('/departments', { params });
    return {
      departments: response.data.departments || [],
      total: response.data.total || 0
    };
  },

  // Get department by ID with full details
  getDepartmentById: async (id) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  // Create new department
  createDepartment: async (departmentData) => {
    const response = await api.post('/departments', departmentData);
    return response.data;
  },

  // Update department
  updateDepartment: async (id, departmentData) => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },

  // Delete department (soft delete)
  deleteDepartment: async (id) => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },

  // Get department hierarchy (tree structure)
  getDepartmentHierarchy: async () => {
    const response = await api.get('/departments/hierarchy');
    return {
      hierarchy: response.data.hierarchy || [],
      total: response.data.total || 0
    };
  },

  // Get department statistics
  getDepartmentStats: async (id) => {
    const response = await api.get(`/departments/${id}/stats`);
    return response.data.stats || {};
  },

  // Get team members in department
  getTeamMembers: async (id, includeSubDepartments = false) => {
    const response = await api.get(`/departments/${id}/team-members`, {
      params: { include_sub_departments: includeSubDepartments }
    });
    return {
      team_members: response.data.team_members || [],
      total: response.data.total || 0
    };
  },

  // Update department budget
  updateBudget: async (id, budgetData) => {
    const response = await api.put(`/departments/${id}/budget`, budgetData);
    return response.data;
  },

  // Helper: Build department tree from flat list
  buildDepartmentTree: (departments, parentId = null) => {
    return departments
      .filter(dept => dept.parent_department_id === parentId)
      .map(dept => ({
        ...dept,
        children: departmentService.buildDepartmentTree(departments, dept.id)
      }));
  },

  // Helper: Calculate budget utilization percentage
  calculateBudgetUtilization: (budgetAllocated, budgetSpent) => {
    if (!budgetAllocated || budgetAllocated === 0) return 0;
    return ((budgetSpent / budgetAllocated) * 100).toFixed(2);
  },

  // Helper: Get budget status (good, warning, critical)
  getBudgetStatus: (utilization) => {
    if (utilization < 70) return 'good';
    if (utilization < 90) return 'warning';
    return 'critical';
  },

  // Helper: Get budget status color
  getBudgetStatusColor: (utilization) => {
    if (utilization < 70) return '#10b981'; // green
    if (utilization < 90) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  },

  // Helper: Format currency
  formatBudget: (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  },

  // Helper: Get department path (breadcrumb trail)
  getDepartmentPath: (departments, departmentId) => {
    const path = [];
    let currentId = departmentId;

    while (currentId) {
      const dept = departments.find(d => d.id === currentId);
      if (!dept) break;
      
      path.unshift({
        id: dept.id,
        name: dept.name,
        code: dept.code
      });
      
      currentId = dept.parent_department_id;
    }

    return path;
  },

  // Helper: Format department path as string
  formatDepartmentPath: (path) => {
    return path.map(p => p.name).join(' > ');
  },

  // Helper: Count team members recursively
  countTeamMembers: (department) => {
    let count = department.employees?.length || 0;
    
    if (department.sub_departments) {
      department.sub_departments.forEach(subDept => {
        count += departmentService.countTeamMembers(subDept);
      });
    }
    
    return count;
  },

  // Helper: Count sub-departments recursively
  countSubDepartments: (department) => {
    let count = department.sub_departments?.length || 0;
    
    if (department.sub_departments) {
      department.sub_departments.forEach(subDept => {
        count += departmentService.countSubDepartments(subDept);
      });
    }
    
    return count;
  },

  // Helper: Flatten department tree to list
  flattenDepartmentTree: (departments, result = []) => {
    departments.forEach(dept => {
      const { children, sub_departments, ...deptData } = dept;
      result.push(deptData);
      
      const childDepts = children || sub_departments;
      if (childDepts && childDepts.length > 0) {
        departmentService.flattenDepartmentTree(childDepts, result);
      }
    });
    
    return result;
  },

  // Helper: Find department by ID in tree
  findDepartmentInTree: (departments, departmentId) => {
    for (const dept of departments) {
      if (dept.id === departmentId) {
        return dept;
      }
      
      const childDepts = dept.children || dept.sub_departments;
      if (childDepts && childDepts.length > 0) {
        const found = departmentService.findDepartmentInTree(childDepts, departmentId);
        if (found) return found;
      }
    }
    
    return null;
  },

  // Helper: Get all parent department IDs
  getParentDepartmentIds: (departments, departmentId) => {
    const parentIds = [];
    let currentId = departmentId;

    while (currentId) {
      const dept = departments.find(d => d.id === currentId);
      if (!dept || !dept.parent_department_id) break;
      
      parentIds.push(dept.parent_department_id);
      currentId = dept.parent_department_id;
    }

    return parentIds;
  },

  // Helper: Check if department can be deleted
  canDeleteDepartment: (department) => {
    const hasSubDepartments = department.sub_departments && department.sub_departments.length > 0;
    const hasEmployees = department.employees && department.employees.length > 0;
    
    return !hasSubDepartments && !hasEmployees;
  },

  // Helper: Get department level (depth in hierarchy)
  getDepartmentLevel: (departments, departmentId) => {
    let level = 0;
    let currentId = departmentId;

    while (currentId) {
      const dept = departments.find(d => d.id === currentId);
      if (!dept || !dept.parent_department_id) break;
      
      level++;
      currentId = dept.parent_department_id;
    }

    return level;
  },

  // Helper: Sort departments by name
  sortDepartmentsByName: (departments, ascending = true) => {
    return [...departments].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return ascending ? comparison : -comparison;
    });
  },

  // Helper: Filter departments by search term
  filterDepartments: (departments, searchTerm) => {
    if (!searchTerm) return departments;
    
    const term = searchTerm.toLowerCase();
    return departments.filter(dept => 
      dept.name.toLowerCase().includes(term) ||
      dept.code?.toLowerCase().includes(term) ||
      dept.description?.toLowerCase().includes(term)
    );
  },

  // Helper: Get root departments (no parent)
  getRootDepartments: (departments) => {
    return departments.filter(dept => !dept.parent_department_id);
  },

  // Helper: Calculate total budget for department and sub-departments
  calculateTotalBudget: (department) => {
    let total = {
      allocated: parseFloat(department.budget_allocated || 0),
      spent: parseFloat(department.budget_spent || 0)
    };
    
    if (department.sub_departments) {
      department.sub_departments.forEach(subDept => {
        const subTotal = departmentService.calculateTotalBudget(subDept);
        total.allocated += subTotal.allocated;
        total.spent += subTotal.spent;
      });
    }
    
    return total;
  }
};

export default departmentService;

